import { useState, useEffect, useRef, useContext } from "react";

//Components
import {
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Button,
} from "@material-ui/core";
import { ResizableBox } from "react-resizable";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import SortableTableHeader from "./SortableTableHeader";
import TableColumnShowHide from "./TableColumnShowHide";
import CircularProgress from "@material-ui/core/CircularProgress";

//Styling
import { useStyles } from "../Styles/styles";
import "../Styles/resizable.css";

//Utils
import { numberSort, wordSort, dateSort } from "../utils/utils";
import useWindowSize from "../utils/window-size";
import { IncidentsContext } from "../Contexts/IncidentsContext";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";
import { checkPermission } from "../utils/Roles";
import moment from "moment";

//pass an array of incident IDs then fetch the relevant information for each incident
const LinkedIncidentsTable = ({
  handleIncidentOpening,
  incidents,
  headers,
  showColumn,
  handleShowColumn,
  headerTitle,
  onCloseHandler,
  type,
  incidentId,
  loading,
  removeIncident,
  locked = true,
  userRoles,
}) => {
  const classes = useStyles();
  const windowSize = useWindowSize();
  const [rows, setRows] = useState([]);
  const { incidentDispatch } = useContext(IncidentsContext);
  const { REMOVE_INCIDENT_FROM_LINK } = INCIDENT_ACTIONS;

  useEffect(() => {
    if (Array.isArray(incidents)) {
      let tempRows = [];
      incidents.map(incident => {
        const {
          id,
          incidentNumber,
          callTime,
          officerIncidentSpecifics,
          sealed,
          locked,
        } = incident;
        const canViewLocked = checkPermission(
          userRoles,
          "view_locked_incident",
          "ALL",
          "",
          incidentId
        );
        const canViewSealed = checkPermission(
          userRoles,
          "view_sealed_incident",
          "ALL",
          "",
          incidentId
        );
        const disabled =
          (!canViewLocked && locked) || (!canViewSealed && sealed)
            ? true
            : false;
        const name = incident.classification
          ? incident.classification.name
          : "";
        let primaryOfficerIncidentSpecific = officerIncidentSpecifics?.find(
          ois => ois.primaryOfficer
        );
        let primaryOfficer =
          primaryOfficerIncidentSpecific &&
          primaryOfficerIncidentSpecific?.officer
            ? `${primaryOfficerIncidentSpecific?.officer?.deptAttribute?.ori?.abbreviation}-${primaryOfficerIncidentSpecific?.officer?.deptAttribute?.badge}`
            : "";
        tempRows.push(
          createData(
            id,
            incidentNumber,
            name,
            callTime,
            primaryOfficer,
            disabled
          )
        );
        return null;
      });
      setRows(tempRows);
    }
  }, [incidentId, incidents, userRoles]);

  function createData(
    id,
    incidentNumber,
    name,
    callTime,
    primaryOfficer,
    disabled
  ) {
    return { id, incidentNumber, name, callTime, primaryOfficer, disabled };
  }

  const sortLabel = useRef();
  const [sortDirection, setSortDirection] = useState("asc");
  const [sortedColumn, setSortedColumn] = useState("");
  // const [sortId, setSortId] = useState('')

  const handleSort = (event, columnName, id) => {
    const currentDirection = sortDirection;
    let newDirection;
    setSortedColumn(columnName);
    // setSortId(id)
    if (currentDirection === "asc") {
      setSortDirection("desc");
      newDirection = "desc";
    } else {
      setSortDirection("asc");
      newDirection = "asc";
    }
    sortRows(newDirection, id);
  };

  const sortRows = (direction, id) => {
    let data = rows;
    if (id === "badge" || id === "carNumber" || id === "radioNumber") {
      numberSort(direction, id, data);
    } else if (id === "lastContactedAt") {
      dateSort(direction, id, data);
    } else {
      wordSort(direction, id, data);
    }
    setRows(data);
  };

  const confirmDelete = incident => {
    if (
      window.confirm(
        `Are you sure to want to ${
          type === "linked" ? "unlink" : "unrelate"
        } incident ${incident.incidentNumber}?`
      )
    ) {
      removeIncident(incident.id);
      incidentDispatch({
        type: REMOVE_INCIDENT_FROM_LINK,
        payload: {
          incidentId: incidentId,
          linkedIncidentId: incident.id,
        },
      });
      // setLinkedState(newIncidents)
      // setRows(parseLinkedIncidents(newIncidents))
    } else {
      return null;
    }
  };
  // if (error) return <p>Error: {error}</p>
  return (
    <div style={{ marginBottom: "2vh" }}>
      <div
        style={{
          textAlign: "center",
          backgroundColor: "#5097d6",
          borderTopLeftRadius: "5px",
          borderTopRightRadius: "5px",
        }}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          position="relative"
        >
          <h3
            data-cy="available-officers-title"
            style={{ display: "in-line block", color: "white", margin: "0px" }}
          >
            {headerTitle}
          </h3>
          <TableColumnShowHide
            headers={headers}
            handleShowColumn={handleShowColumn}
            showClose={true}
            onCloseHandler={onCloseHandler}
          />
        </Box>
      </div>
      <ResizableBox
        width={windowSize.width * 0.78}
        height={windowSize.height * 0.3}
        handle={<DragHandleIcon className={`react-resizable-handle`} />}
        handleSize={[20, 20]}
        axis={"y"}
      >
        <TableContainer
          style={{ width: "100%" }}
          className={classes.tableContainer}
          component={Paper}
        >
          {!loading ? (
            <Table
              stickyHeader
              className={classes.table}
              aria-label="simple table"
            >
              <TableHead>
                <TableRow>
                  {headers.map(header => {
                    return (
                      <SortableTableHeader
                        show={header.show}
                        key={header.title}
                        title={header.title}
                        idName={header.idName}
                        handleSort={handleSort}
                        sortLabel={sortLabel}
                        sortedColumn={sortedColumn}
                        sortDirection={sortDirection}
                      />
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody data-cy="availOfficer-table-body">
                {rows.map((row, index) => (
                  <TableRow
                    style={
                      index % 2
                        ? { background: "white" }
                        : { background: "#eee" }
                    }
                    data-cy="availOfficer-table-row"
                    key={row.incidentNumber}
                  >
                    {/* <TableCell style={{ textAlign: 'center', display: showColumn.incidentNumber ? "" : 'none' }} scope="row"><button style={{ marginLeft: row.child ? '30px' : '' }} className={classes.linkButton} data-cy={"tablecell-incident-number-link-" + index} onClick={() => { handleIncidentOpening(row.id, true) }}>{row.incidentNumber}</button></TableCell> */}
                    {/* <TableCell style={{ textAlign: 'center', display: showColumn.incidentNumber ? "" : 'none' }} scope="row">{row.incidentNumber}</TableCell> */}
                    <TableCell
                      style={{
                        textAlign: "center",
                        display: showColumn.incidentNumber ? "" : "none",
                      }}
                      scope="row"
                    >
                      <button
                        style={{ marginLeft: row.child ? "30px" : "" }}
                        className={
                          row?.disabled
                            ? classes.linkButtonDisabled
                            : classes.linkButton
                        }
                        data-cy={"tablecell-incident-number-link-" + index}
                        onClick={
                          row?.disabled
                            ? () =>
                                alert(
                                  "You do not have permission to view this incident"
                                )
                            : () => {
                                handleIncidentOpening(row.id, true);
                              }
                        }
                      >
                        {row.incidentNumber}
                      </button>
                    </TableCell>
                    <TableCell
                      style={{
                        textAlign: "center",
                        display: showColumn.name ? "" : "none",
                      }}
                      scope="row"
                    >
                      {row.name}
                    </TableCell>
                    <TableCell
                      style={{
                        textAlign: "center",
                        display: showColumn.callTime ? "" : "none",
                      }}
                      scope="row"
                    >
                      {row.callTime
                        ? moment
                            .utc(row.callTime, "YYYY-MM-DD HH:mm:ss UTC")
                            .local()
                            .format("MM/DD/YY HH:mm")
                        : ""}
                    </TableCell>
                    <TableCell
                      style={{
                        textAlign: "center",
                        display: showColumn.primaryOfficer ? "" : "none",
                      }}
                      scope="row"
                    >
                      {row.primaryOfficer}
                    </TableCell>
                    {/* <TableCell style={{ textAlign: 'center', display: showColumn.occurredFrom ? "" : 'none' }} scope="row">{moment(row.occurredFrom).format("MM/DD/YY HH:mm")}</TableCell> */}
                    {/* <TableCell style={{ textAlign: 'center', display: showColumn.occurredTo ? "" : 'none' }} scope="row">{moment(row.occurredTo).format("MM/DD/YY HH:mm")}</TableCell> */}
                    <TableCell
                      style={{
                        textAlign: "center",
                        display: showColumn.remove ? "" : "none",
                      }}
                      scope="row"
                    >
                      <Button
                        onClick={
                          !locked && !row.disabled
                            ? () => confirmDelete(row)
                            : null
                        }
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress data-cy="loading-circle" />
            </div>
          )}
          {incidents.length === 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                width: windowSize.width * 0.78,
              }}
            >
              <h3>No {headerTitle}</h3>
            </div>
          )}
        </TableContainer>
      </ResizableBox>
    </div>
  );
};

export default LinkedIncidentsTable;
